import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useFilters,
  useIsEditing,
  useIsExcelDownloading,
  useSetFilter,
  useTableStore,
} from "@/pages/Project/Estimate/hooks/useTableStore";
import { ItemSheetExtraTag } from "@/consts/Estimate";
import { ItemSheetObject } from "@/types/itemsheet";
import EstimateAuditLogsDialog from "../EstimateAuditLogsDialog";
import { useMemo } from "react";
import MultiSelect from "@/components/Common/MultiSelect";

type Props = {
  latestsSheetData: ItemSheetObject | undefined;
};

export default function EstimateFilters({ latestsSheetData }: Props) {
  const isEditing = useIsEditing();
  const filters = useFilters();
  const isExcelDownloading = useIsExcelDownloading();
  const setFilter = useSetFilter();

  const { manufacturers, vendors, buyers } = useMemo(() => {
    const rows = latestsSheetData?.data ?? [];
    return {
      manufacturers: Array.from(
        new Set(rows.map((row) => row.manufacturer))
      ).sort(),
      vendors: Array.from(new Set(rows.map((row) => row.vendor))).sort(),
      buyers: Array.from(new Set(rows.map((row) => row.buyer))).sort(),
    };
  }, [latestsSheetData]);

  const ownProductOptions = [
    { label: "전체", value: "all" },
    { label: "자사 제품", value: "true" },
    { label: "타사 제품", value: "false" },
  ];

  const targetPriceOptions = [
    { label: "전체", value: "all" },
    { label: "Custom price", value: ItemSheetExtraTag.CUSTOM_PRICE },
    { label: "Floor price", value: ItemSheetExtraTag.FLOOR_PRICE },
  ];

  const renderSelect = ({
    value,
    onChangeKey,
    placeholder,
    options,
  }: {
    value: string;
    onChangeKey: keyof typeof filters;
    placeholder: string;
    options: { label: string; value: string }[];
  }) => (
    <Select
      value={value}
      onValueChange={(selected) => setFilter(onChangeKey, selected)}
      disabled={isEditing || isExcelDownloading}
    >
      <SelectTrigger className="min-w-36">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  // 빈 값이 있는지 확인하는 함수
  const hasEmptyValues = (items: (string | null | undefined)[]) => {
    return items.some((item) => !item || item === "");
  };

  return (
    <div className="inline-flex items-center gap-[10px] w-fit">
      {renderSelect({
        value: filters.ownProduct,
        onChangeKey: "ownProduct",
        placeholder: "자사 제품 여부",
        options: ownProductOptions,
      })}

      <MultiSelect
        options={manufacturers.filter((m): m is string => !!m)}
        selected={filters.manufacturer}
        onChange={(selected) => setFilter("manufacturer", selected)}
        hasEmptyOption={true}
        placeholder="제조사"
        disabled={isEditing || isExcelDownloading}
      />

      <MultiSelect
        options={vendors.filter((v): v is string => !!v)}
        selected={filters.vendor}
        onChange={(selected) => setFilter("vendor", selected)}
        hasEmptyOption={hasEmptyValues(vendors)}
        placeholder="벤더"
        disabled={isEditing || isExcelDownloading}
      />

      <MultiSelect
        options={buyers.filter((b): b is string => !!b)}
        selected={filters.buyer}
        onChange={(selected) => setFilter("buyer", selected)}
        hasEmptyOption={hasEmptyValues(buyers)}
        placeholder="구매업체"
        disabled={isEditing || isExcelDownloading}
      />

      {renderSelect({
        value: filters.targetPrice,
        onChangeKey: "targetPrice",
        placeholder: "가격 정책",
        options: targetPriceOptions,
      })}

      <EstimateAuditLogsDialog />
    </div>
  );
}
